import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, matchRoutes } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  usePaymentActivityById,
  useFetchPaymentActivityById,
  useReversePayment,
  formatPaymentActivity,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { useAllRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { useCreateBulkReversal } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useDeleteScheduledACHPayment } from "byzantine/src/dbbl/businessLogic/modules/achPayments";

import { useNotificationContext } from "cerulean";
import type { ReversalReason } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useSudoContext } from "../../SudoContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import SudoDialog from "../../SudoDialog";
import ReversePaymentModal from "./ReversePaymentModal";
import {
  PAYMENT_ACTIVITY_ROUTE,
  getReverseRoute,
  getReverseFromListRoute,
} from "../routes";

type ReverseFlowProps = {
  activityUuid: string;
  keepDrawerOpen?: boolean;
  onComplete?: () => void;
};

/**
 * url driven flow for the reverse payment flow (including sudo)
 * mounted by IndividualPaymentContainer on one of two URL shapes:
 * - drawer entry: /payment_activity/:activityUuid/reverse (keepDrawerOpen, drawer stays open)
 * - list entry:   /payment_activity/reverse/:reverseActivityUuid (modal only)
 * it resolves the payment by uuid store first, then a fetch for deep links, and gates the modal on
 * eligibility, or the reversals feature being off, and redirects back to the payment drawer
 *
 * SudoDialog renders the OTP step inline as a modal
 * the reversal form hides (isOpen=false) and SudoDialog shows
 * once sudo is established the callback flips showReceipt and
 * navigation returns to REVERSE_ROUTE, reopening the modal (and displaying receipt screen)
 */

const ReverseFlow = ({
  activityUuid,
  keepDrawerOpen = false,
  onComplete,
}: ReverseFlowProps) => {
  const { establishSudo, requestOtpRoute, submitOtpRoute } = useSudoContext();
  const { l10n } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const institution = useInstitutionSettings();
  const recipients = useAllRecipients();
  const accounts = useAllAccounts();
  const paymentsById = usePaymentActivityById();
  const { send: fetchPaymentActivityById } = useFetchPaymentActivityById();
  const { send: reversePayment } = useReversePayment();
  const { send: createBulkReversal } = useCreateBulkReversal();
  const { deleteScheduledACHPayment } = useDeleteScheduledACHPayment();
  const { sendNotification } = useNotificationContext();
  const [showReceipt, setShowReceipt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reversalDualApprovalId, setReversalDualApprovalId] =
    useState<API.DualApprovalId | null>(null);
  const [fetchedPaymentActivity, setFetchedPaymentActivity] = useState<
    API.PaymentActivity | null | undefined
  >(undefined);

  const REVERSE_ROUTE = keepDrawerOpen
    ? getReverseRoute(activityUuid)
    : getReverseFromListRoute(activityUuid);
  const drawerRoute = `/${PAYMENT_ACTIVITY_ROUTE}/${activityUuid}`;
  const dismissRoute = keepDrawerOpen
    ? drawerRoute
    : `/${PAYMENT_ACTIVITY_ROUTE}`;

  // store hit (payment already loaded via the paginated list) skips the fetch;
  // deep links fall back to a fetch by uuid. this fetch does not populate the store,
  // so we keep the raw result locally
  const cachedPaymentActivity = paymentsById[activityUuid] as
    | API.PaymentActivity
    | undefined;

  useEffect(() => {
    if (cachedPaymentActivity) return undefined;
    let cancelled = false;
    setFetchedPaymentActivity(undefined);
    fetchPaymentActivityById(
      activityUuid as API.ScheduledACHPaymentId | API.ACHPaymentId
    )
      .then((raw) => {
        if (cancelled) return;
        setFetchedPaymentActivity((raw as API.PaymentActivity) ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedPaymentActivity(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activityUuid, cachedPaymentActivity, fetchPaymentActivityById]);

  const rawPayment = cachedPaymentActivity ?? fetchedPaymentActivity ?? null;
  const isResolved =
    Boolean(cachedPaymentActivity) || fetchedPaymentActivity !== undefined;
  const payment = useMemo(
    () =>
      rawPayment
        ? formatPaymentActivity(rawPayment, recipients, accounts)
        : null,
    [rawPayment, recipients, accounts]
  );

  const isEligible =
    Boolean(institution.features?.user_initiated_ach_reversals) &&
    Boolean(payment?.isReversible);

  const isSudoRoute = Boolean(
    matchRoutes(
      [{ path: requestOtpRoute }, { path: submitOtpRoute }],
      location.pathname
    )
  );

  // not eligible so bail out of the reverse flow back to wherever it was launched from
  useEffect(() => {
    if (isResolved && !isEligible && !showReceipt) {
      navigate(`${dismissRoute}${location.search}`, { replace: true });
    }
  }, [
    isResolved,
    isEligible,
    showReceipt,
    dismissRoute,
    location.search,
    navigate,
  ]);

  const sudoRequired = Boolean(
    institution.sudo_mode_required_for_user_initiated_ach_reversals
  );

  const executeReversal = async (
    reason: ReversalReason,
    cancelRecurring: boolean
  ) => {
    if (!payment?.uuid || submitting) return;
    setSubmitting(true);

    const cancelRecurringIfRequested = async () => {
      if (cancelRecurring && payment.parent) {
        try {
          await deleteScheduledACHPayment(
            payment.parent as API.ScheduledACHPaymentId,
            (inner) => ({
              ...inner,
              onSuccess: async () => {
                sendNotification({
                  type: "success",
                  text: l10n.getString(
                    "reverse-payment-cancel-recurring-success",
                    {},
                    "Remaining recurring payments canceled."
                  ),
                });
              },
            })
          );
        } catch {
          // the delete hook surfaces its own error toast; swallow here so a
          // failed recurring-cancel still shows the reversal receipt
        }
      }
    };

    // a payment that belongs to a bulk must be reversed via the bulk_reversal endpoint
    if (payment.bulkPaymentId) {
      try {
        const bulkReversal = await createBulkReversal({
          bulkPaymentId: payment.bulkPaymentId as API.BulkPaymentId,
          reversalReason: reason,
          transactionIds: [payment.uuid as API.ACHPaymentId],
        });
        setReversalDualApprovalId(bulkReversal?.dual_approval_id ?? null);
      } catch {
        setSubmitting(false);
        return;
      }
      await cancelRecurringIfRequested();
      setShowReceipt(true);
      setSubmitting(false);
      return;
    }

    const payload: API.CreateACHReversal.Request = {
      reversal_target_transaction: payment.uuid as API.ACHPaymentId,
      reversal_reason: reason,
    };

    try {
      const reversal = await reversePayment(payload);
      setReversalDualApprovalId(reversal?.dual_approval_id ?? null);
    } catch (error: unknown) {
      let message = l10n.getString("error-generic");
      try {
        const json = await (
          error as {
            response?: { json: () => Promise<Record<string, string[]>> };
          }
        )?.response?.json();
        if (json) {
          const firstError = Object.values(json).flat()[0];
          if (firstError) {
            message = firstError;
          }
        }
      } catch {
        // fall through to generic message
      }
      sendNotification({
        type: "negative",
        text: message,
      });
      setSubmitting(false);
      return;
    }

    await cancelRecurringIfRequested();

    setShowReceipt(true);
    setSubmitting(false);
  };

  const handleReversePayment = (
    reason: ReversalReason,
    cancelRecurring: boolean
  ) => {
    if (sudoRequired) {
      establishSudo(REVERSE_ROUTE, () =>
        executeReversal(reason, cancelRecurring)
      ).catch(() => {
        // sudo was cancelled or failed, stay on the reversal form
      });
    } else {
      executeReversal(reason, cancelRecurring);
    }
  };

  const handleClose = () => {
    if (showReceipt && onComplete) {
      onComplete();
    } else {
      // return to wherever the flow was launched from (open drawer vs table)
      navigate(`${dismissRoute}${location.search}`);
    }
  };

  return (
    <>
      <ReversePaymentModal
        isOpen={!isSudoRoute && (isEligible || showReceipt)}
        reversiblePayment={payment ? { payment } : null}
        showReceipt={showReceipt}
        handleClose={handleClose}
        handleReversePayment={handleReversePayment}
        submitting={submitting}
        reversalDualApprovalId={reversalDualApprovalId}
      />
      <SudoDialog />
    </>
  );
};

export default ReverseFlow;
